import { useEffect, useState } from "react";
import { CheckCircle, ArrowCounterClockwise, PaperPlaneRight, SpinnerGap, ArrowBendDownRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";

const ta = "w-full rounded-lg border border-[#302B28] bg-[#1E1A18] p-3 text-sm text-[#F9F7F4] outline-none focus:border-[#A39E9A]";

function Item({ m, onChange }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/messages/${m.message_id}/reply`, { body: reply });
      onChange({ ...m, status: "handled", replies: [...m.replies, r.data.reply] }); setReply("");
      toast[r.data.emailed ? "success" : "warning"](r.data.emailed ? `Reply emailed to ${m.user_name}` : "Reply saved, but the email could not be sent right now");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = async () => {
    const status = m.status === "open" ? "handled" : "open";
    try { await api.patch(`/admin/messages/${m.message_id}`, { status }); onChange({ ...m, status }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <article className="rounded-2xl border border-[#302B28] bg-[#1E1A18]/60 p-5" data-testid={`inbox-item-${m.message_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium">{m.user_name} <span className="text-sm font-normal text-[#A39E9A]">{m.user_email}</span></p>
          <p className="text-xs text-[#A39E9A]">{fmtDate(m.created_at)} · {m.source === "welcome" ? "Reply to welcome note" : "From dashboard"}</p>
        </div>
        <button onClick={toggle} data-testid={`inbox-toggle-${m.message_id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${m.status === "open" ? "bg-[#3A2A14] text-[#F2C46B]" : "bg-[#1D3324] text-[#8BD3A0]"}`}>
          {m.status === "open" ? <><CheckCircle /> Mark handled</> : <><ArrowCounterClockwise /> Reopen</>}
        </button>
      </div>
      <p className="mt-3 whitespace-pre-wrap text-sm">{m.body}</p>
      {m.replies.map((r, i) => (
        <div key={i} className="mt-3 flex gap-2 rounded-xl bg-[#151312] p-3 text-sm"><ArrowBendDownRight className="mt-0.5 shrink-0 text-[#A39E9A]" /><div><p className="text-xs text-[#A39E9A]">{r.by} · {fmtDate(r.at)}</p><p className="whitespace-pre-wrap">{r.body}</p></div></div>
      ))}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
        <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={`Reply to ${m.user_name.split(" ")[0]} by email…`} className={ta} data-testid={`inbox-reply-input-${m.message_id}`} />
        <button onClick={send} disabled={busy || !reply.trim()} data-testid={`inbox-reply-send-${m.message_id}`} className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-[#F9F7F4] px-4 text-sm font-semibold text-[#151312] disabled:opacity-40">
          {busy ? <SpinnerGap className="animate-spin" /> : <PaperPlaneRight />} Send reply
        </button>
      </div>
    </article>
  );
}

export default function Inbox() {
  const [filter, setFilter] = useState("open");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { setErr(""); api.get("/admin/messages", { params: { status: filter } }).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e))); }, [filter]);
  const update = (m) => setData((d) => {
    const prev = d.items.find((x) => x.message_id === m.message_id);
    const open = d.open + (prev?.status === m.status ? 0 : m.status === "open" ? 1 : -1);
    const items = d.items.map((x) => (x.message_id === m.message_id ? m : x)).filter((x) => !filter || x.status === filter);
    return { ...d, items, open };
  });
  return (
    <div data-testid="admin-inbox">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="font-display text-3xl font-medium tracking-tight">Inbox</h1><p className="mt-1 text-sm text-[#A39E9A]">Messages from hosts, including replies to the welcome email. <span data-testid="inbox-open-count">{data?.open ?? 0}</span> open.</p></div>
        <div className="flex gap-1 rounded-lg border border-[#302B28] p-1">
          {[["open", "Open"], ["handled", "Handled"], ["", "All"]].map(([k, l]) => (
            <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`inbox-filter-${k || "all"}`} className={`rounded-md px-3 py-1.5 text-sm ${filter === k ? "bg-[#F9F7F4] text-[#151312]" : "text-[#A39E9A]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-6 space-y-3">
        {err && <p className="rounded-xl bg-[#3A1F18] px-4 py-3 text-sm text-[#F08A68]" data-testid="inbox-error">{err}</p>}
        {data?.items.length === 0 && <p className="text-sm text-[#A39E9A]" data-testid="inbox-empty">No messages here.</p>}
        {data?.items.map((m) => <Item key={m.message_id} m={m} onChange={update} />)}
      </div>
    </div>
  );
}
